import { BottomSheet } from '@/shared/components';
import { cn } from '@/shared/lib/cn';
import { BookingDetailActions, DetailsChannelMark, PlanBanner } from './BookingDetailActions';
import type { BookingDetailsModel } from './bookingDetailsModel';
import type { BookingSpan } from './calendarHelpers';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  span: BookingSpan;
  model: BookingDetailsModel;
  propertyId: string;
  canOpenOnboarding: boolean;
  canReassign?: boolean;
  onReassignRequested?: () => void;
}

const contactButton =
  'border-line rounded-control flex h-12 items-center justify-center border text-sm font-bold';

/**
 * Dieselben Details als Blatt am Handy (Board „Belegung · Handy · Buchung angetippt“): Fakten als
 * Kacheln, Anrufen und E-Mail als große Ziele – nur mit Gastdaten-Recht –, das Plan-Banner und die
 * nächsten Schritte untereinander.
 */
export function BookingDetailsSheet({
  open,
  onOpenChange,
  span,
  model,
  propertyId,
  ...rights
}: Props) {
  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      title={
        <span className="flex items-center gap-2.5">
          <DetailsChannelMark span={span} size={34} />
          <span className="min-w-0 truncate">{model.title}</span>
        </span>
      }
      description={<span className="font-mono text-[11px]">{model.sub}</span>}
    >
      <div className="flex flex-col gap-3 pb-2">
        <div className="grid grid-cols-2 gap-2">
          {model.facts.map((fact) => (
            <div key={fact.label} className="bg-paper rounded-control flex flex-col px-3 py-2">
              <span className="text-ink-soft text-[11px]">{fact.label}</span>
              <span className="text-sm font-bold">{fact.value}</span>
            </div>
          ))}
        </div>
        {model.contacts && (
          <div className="grid grid-cols-2 gap-2">
            {model.contacts.phone ? (
              <a href={`tel:${model.contacts.phone}`} className={contactButton}>
                Anrufen
              </a>
            ) : (
              <span className={cn(contactButton, 'text-ink-faint font-semibold')}>
                Telefon nicht hinterlegt
              </span>
            )}
            {model.contacts.email !== undefined && (
              <>
                {model.contacts.email ? (
                  <a href={`mailto:${model.contacts.email}`} className={contactButton}>
                    E-Mail
                  </a>
                ) : (
                  <span className={cn(contactButton, 'text-ink-faint font-semibold')}>
                    E-Mail nicht hinterlegt
                  </span>
                )}
              </>
            )}
          </div>
        )}
        {model.plan && <PlanBanner plan={model.plan} className="px-3 py-2.5 text-[13px]" />}
        <BookingDetailActions span={span} propertyId={propertyId} variant="sheet" {...rights} />
      </div>
    </BottomSheet>
  );
}
